/* eslint-disable camelcase */
import { Amplify } from 'aws-amplify'
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updatePassword,
  updateUserAttributes,
  type FetchUserAttributesOutput
} from 'aws-amplify/auth'
import googleService from './google'
import { isEmailValid, sanitizeTelephone } from '../helpers'
import { ISignUpState } from '../store/states'
import { appsUsernameSelected } from '../helpers/app'

function cognitoAttributesToBodUser(attributes: FetchUserAttributesOutput) {
  return {
    email: attributes.email || '',
    name: attributes.name || '',
    lastName:
      attributes.family_name ||
      attributes['custom:last_name'] ||
      '',
    dni: attributes['custom:dni_nie'] || ''
  }
}

class AuthenticationService {
  constructor() {
    Amplify.configure({
      Auth: {
        Cognito: {
          // identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID || '',
          userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
          userPoolClientId: process.env.REACT_APP_USER_POOL_WEBCLIENT_ID || ''
        }
      }
    })
  }

  public async signIn(args: any): Promise<{ challengeName?: any }> {
    const username = isEmailValid(args.username)
      ? args.username.toLowerCase()
      : args.username
    const result = await signIn({ username, password: args.password })
    // Map v6 nextStep to v3-compatible challengeName
    const step = result.nextStep?.signInStep
    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { challengeName: 'NEW_PASSWORD_REQUIRED' }
    }
    return result as any
  }

  public signUp(args: ISignUpState): Promise<any> {
    const {
      name, lastName, email, dni, password, phone, callingCode,
      birthDay, birthMonth, birthYear, gender, isPrm, settlement,
      additionalPhone, additionalPhoneCallingCode
    } = args
    // Brands that register by phone identify the account by it, and may have no email.
    const isPhoneSignUp = appsUsernameSelected?.username === 'phone'
    const phoneNumber = isPhoneSignUp ? sanitizeTelephone((phone || '').trim(), callingCode) : ''
    const attributes: any = {
      name,
      family_name: lastName,
      'custom:dni_nie': dni
    }
    if (email) attributes.email = email.toLowerCase()
    if (phoneNumber) attributes.phone_number = phoneNumber
    if (birthDay && birthMonth && birthYear) {
      const day = birthDay.length === 1 ? `0${birthDay}` : birthDay
      const month = birthMonth.length === 1 ? `0${birthMonth}` : birthMonth
      attributes['custom:birthdate'] = `${birthYear}-${month}-${day}`
    }
    if (gender) attributes['custom:gender'] = gender
    if (isPrm) attributes['custom:is_prm'] = 'true'
    if (settlement) attributes['custom:village'] = settlement
    if (additionalPhone) {
      attributes['custom:additional_phone'] = sanitizeTelephone(additionalPhone, additionalPhoneCallingCode)
    }
    const signUpData: any = {
      username: isPhoneSignUp ? phoneNumber : dni,
      password,
      attributes
    }
    return signUp(signUpData)
  }

  public resendSignUpConfirmationCode(dni: string) {
    return resendSignUpCode({ username: dni })
  }

  public confirmSignUp(dni: string, code: string): Promise<any> {
    return confirmSignUp({ username: dni, confirmationCode: code })
  }

  public async signOut(): Promise<any> {
    googleService.clear()
    return signOut()
  }

  public async getTokens(): Promise<any> {
    try {
      const session = await fetchAuthSession()
      if (session.tokens?.idToken) return session.tokens
    } catch (error) {
      // No Cognito session — fall through to a Google one.
    }

    // Google login: Google's access token is opaque, so the gateway validates the id_token.
    const googleIdToken = googleService.getIdToken()
    if (googleIdToken) {
      const wrapped = { toString: () => googleIdToken }
      return { idToken: wrapped, accessToken: wrapped }
    }

    return undefined
  }

  public requestRecoverPasswordCode(email: string): Promise<any> {
    return resetPassword({ username: email })
  }

  public requestRecoverPasswordChange(
    email: string,
    code: string,
    password: string
  ): Promise<any> {
    return confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword: password
    })
  }

  public async completeNewPassword(user: any, password: string): Promise<any> {
    return confirmSignIn({ challengeResponse: password })
  }

  public async getUserAttributes(): Promise<any> {
    const attributes = await fetchUserAttributes()
    return cognitoAttributesToBodUser(attributes)
  }

  public async changePassword(
    password: string,
    newPassword: string
  ): Promise<any> {
    return updatePassword({ oldPassword: password, newPassword })
  }

  public async updateAttributes(attributes: any): Promise<any> {
    await updateUserAttributes({
      userAttributes: attributes
    })
    return this.getUserAttributes()
  }
}

export default new AuthenticationService()
